'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useId, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { Button } from '../../actions/button/index.js';
import { Alert } from '../../feedback/alert/index.js';
import { Checkbox } from '../checkbox/index.js';
import { Field, FieldDescription, FieldError, FieldLabel, NativeSelect, } from '../form-field/index.js';
import { Switch } from '../switch/index.js';
import { Input, Textarea } from '../text-field/index.js';
import { DatePicker } from '../../scheduling/date-picker/index.js';
const DEFAULT_MESSAGES = {
    required: 'This field is required.',
    mustAccept: 'Turn this on to continue.',
    minLength: (n) => `Use at least ${n} characters.`,
    maxLength: (n) => `Use ${n} characters or fewer.`,
    pattern: 'Check the format.',
    number: 'Enter a number.',
    integer: 'Enter a whole number.',
    min: (n) => `Enter ${n} or more.`,
    max: (n) => `Enter ${n} or less.`,
    choose: 'Choose…',
    submit: 'Save',
    submitFailed: 'The form could not be saved. Try again.',
    yes: 'Yes',
    no: 'No',
    empty: '—',
};
const isSection = (item) => item.type === 'section';
/** Every field in schema order, sections flattened. */
function allFields(schema) {
    return schema.flatMap((item) => item.type === 'section' ? [...item.fields] : [item]);
}
const isEmpty = (v) => v === null || v === undefined || v === '';
function emptyValue(field) {
    if (field.type === 'checkbox' || field.type === 'switch')
        return field.defaultValue ?? false;
    if (field.type === 'number' || field.type === 'date')
        return field.defaultValue ?? null;
    return field.defaultValue ?? '';
}
/** The starting values of a schema: each field's `defaultValue`, or its empty value. */
export function schemaDefaultValues(schema, values = {}) {
    const out = {};
    for (const field of allFields(schema))
        out[field.name] = values[field.name] ?? emptyValue(field);
    return out;
}
/** Whether a field or section with this condition is shown for these values. */
export function isSchemaFieldVisible(condition, values) {
    if (!condition)
        return true;
    if (typeof condition === 'function')
        return condition(values);
    const value = values[condition.field] ?? null;
    if ('equals' in condition && value !== condition.equals)
        return false;
    if ('notEquals' in condition && value === condition.notEquals)
        return false;
    if (condition.oneOf && !condition.oneOf.includes(value))
        return false;
    if (condition.filled !== undefined && condition.filled === (isEmpty(value) || value === false))
        return false;
    return true;
}
/** The fields shown for these values, with their section's condition applied. */
function visibleFields(schema, values) {
    return schema.flatMap((item) => {
        if (item.type === 'section') {
            if (!isSchemaFieldVisible(item.visibleWhen, values))
                return [];
            return item.fields.filter((f) => isSchemaFieldVisible(f.visibleWhen, values));
        }
        const field = item;
        return isSchemaFieldVisible(field.visibleWhen, values) ? [field] : [];
    });
}
/** Checks one field's value. Returns the message, or `null` when it is valid. */
export function validateSchemaField(field, value, values, messagesInput = {}) {
    const m = { ...DEFAULT_MESSAGES, ...messagesInput };
    if (field.type === 'checkbox' || field.type === 'switch') {
        if (field.required && value !== true)
            return m.mustAccept;
    }
    else if (isEmpty(value)) {
        if (field.required)
            return m.required;
        return null;
    }
    else if (field.type === 'text' || field.type === 'textarea') {
        const text = String(value);
        if (field.minLength !== undefined && text.length < field.minLength)
            return m.minLength(field.minLength);
        if (field.maxLength !== undefined && text.length > field.maxLength)
            return m.maxLength(field.maxLength);
        if (field.type === 'text' && field.pattern && !new RegExp(`^(?:${field.pattern})$`).test(text))
            return field.patternMessage ?? m.pattern;
    }
    else if (field.type === 'number') {
        if (typeof value !== 'number' || Number.isNaN(value))
            return m.number;
        if (field.integer && !Number.isInteger(value))
            return m.integer;
        if (field.min !== undefined && value < field.min)
            return m.min(field.min);
        if (field.max !== undefined && value > field.max)
            return m.max(field.max);
    }
    return field.validate?.(value, values) || null;
}
/**
 * Validates every visible field. Returns the errors keyed by field name; an
 * empty object means the values can be submitted.
 */
export function validateSchemaForm(schema, values, messages = {}) {
    const errors = {};
    for (const field of visibleFields(schema, values)) {
        const error = validateSchemaField(field, values[field.name] ?? null, values, messages);
        if (error)
            errors[field.name] = error;
    }
    return errors;
}
/**
 * A field's value as display text, for read-only views, tables and
 * summaries: option labels for selects, Yes/No for booleans, a locale date,
 * a locale number, and "—" for empty values.
 */
export function formatSchemaValue(field, value, options = {}) {
    const m = { ...DEFAULT_MESSAGES, ...options.messages };
    if (field.type === 'checkbox' || field.type === 'switch')
        return value === true ? m.yes : m.no;
    if (isEmpty(value ?? null))
        return m.empty;
    if (field.type === 'select')
        return field.options.find((o) => o.value === value)?.label ?? String(value);
    if (field.type === 'number' && typeof value === 'number')
        return new Intl.NumberFormat(options.locale).format(value);
    if (field.type === 'date' && typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [y, mo, d] = value.split('-').map(Number);
        return new Intl.DateTimeFormat(options.locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(Date.UTC(y, mo - 1, d)));
    }
    if (field.type === 'text' && field.inputType === 'password')
        return '••••••••';
    return String(value);
}
/** Text the number box shows for a value. */
const numberText = (v) => (typeof v === 'number' ? String(v) : '');
function parseNumber(text) {
    const t = text.trim().replace(',', '.');
    if (t === '')
        return null;
    return /^[-+]?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?$/i.test(t) ? Number(t) : Number.NaN;
}
/** A text box for numbers that keeps what is typed ("-", "1.") while the value is a number. */
function NumberControl({ field, value, onValue, onBlur }) {
    const [text, setText] = useState(() => numberText(value));
    useEffect(() => {
        // Follow outside changes, but keep partial text that parses to the same value.
        const parsed = parseNumber(text);
        if (!(parsed === value || (Number.isNaN(parsed) && Number.isNaN(value))))
            setText(numberText(value));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    return (_jsx(Input, { name: field.name, inputMode: field.integer ? 'numeric' : 'decimal', value: text, placeholder: field.placeholder, autoComplete: "off", onChange: (e) => {
            setText(e.target.value);
            onValue(parseNumber(e.target.value));
        }, onBlur: onBlur }));
}
/**
 * Renders a form from a field schema: text, number, select, checkbox, switch,
 * date and textarea fields, with required and length, pattern and range
 * rules, titled sections and conditional visibility. Built on Field, so
 * labels, help and errors are wired for assistive technology. On submit it
 * validates, focuses the first invalid field, and passes the visible values.
 */
export const SchemaDrivenForm = forwardRef(function SchemaDrivenForm({ schema, values: valuesProp, defaultValues, onValuesChange, onSubmit, errors: externalErrors, validateOn = 'blur', submitLabel, secondaryActions, disabled = false, readOnly = false, locale, messages: messagesProp, className, ...rest }, ref) {
    const messages = { ...DEFAULT_MESSAGES, ...messagesProp };
    const [inner, setInner] = useState(() => schemaDefaultValues(schema, defaultValues));
    const controlled = valuesProp !== undefined;
    const values = controlled ? schemaDefaultValues(schema, valuesProp) : inner;
    const valuesRef = useRef(values);
    valuesRef.current = values;
    const [touched, setTouched] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [pending, setPending] = useState(false);
    const [formError, setFormError] = useState(null);
    const [dateErrors, setDateErrors] = useState({});
    const formRef = useRef(null);
    const auto = useId();
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    const setValue = (name, value) => {
        const next = { ...valuesRef.current, [name]: value };
        valuesRef.current = next;
        if (!controlled)
            setInner(next);
        onValuesChange?.(next, name);
    };
    const touch = (name) => {
        if (validateOn === 'blur')
            setTouched((t) => (t[name] ? t : { ...t, [name]: true }));
    };
    const clientErrors = validateSchemaForm(schema, values, messages);
    const errorFor = (name) => externalErrors?.[name] ?? (submitted || touched[name] ? clientErrors[name] : null);
    const submit = async (event) => {
        event.preventDefault();
        if (pending || disabled || readOnly)
            return;
        setSubmitted(true);
        setFormError(null);
        const shown = visibleFields(schema, values);
        const firstInvalid = shown.find((f) => clientErrors[f.name] || dateErrors[f.name]);
        if (firstInvalid) {
            const wrapper = Array.from(formRef.current?.querySelectorAll('[data-schema-field]') ?? []).find((node) => node.dataset.schemaField === firstInvalid.name);
            const el = wrapper?.querySelector('input, textarea, select, button');
            el?.focus();
            return;
        }
        const out = {};
        for (const f of shown)
            out[f.name] = values[f.name] ?? null;
        setPending(true);
        try {
            await onSubmit?.(out);
        }
        catch (error) {
            if (mounted.current)
                setFormError(error instanceof Error && error.message ? error.message : messages.submitFailed);
        }
        finally {
            if (mounted.current)
                setPending(false);
        }
    };
    const renderField = (field) => {
        if (!isSchemaFieldVisible(field.visibleWhen, values))
            return null;
        const value = values[field.name] ?? null;
        const error = errorFor(field.name);
        const fieldDisabled = disabled || field.disabled || pending;
        const common = {
            'data-schema-field': field.name,
            'data-type': field.type,
            className: 'mn-schema-driven-form__field',
            invalid: Boolean(error) || Boolean(dateErrors[field.name]),
            required: Boolean(field.required),
            disabled: fieldDisabled,
        };
        const label = _jsx(FieldLabel, { children: field.label });
        const help = field.description ? (_jsx(FieldDescription, { children: field.description })) : null;
        const err = _jsx(FieldError, { children: error });
        switch (field.type) {
            case 'checkbox':
            case 'switch': {
                const Control = field.type === 'checkbox' ? Checkbox : Switch;
                return (_jsxs(Field, { ...common, children: [_jsx(Control, { name: field.name, label: field.label, ...(field.description ? { description: field.description } : {}), checked: value === true, onCheckedChange: (c) => {
                                setValue(field.name, c === true);
                                touch(field.name);
                            } }), err] }, field.name));
            }
            case 'select':
                return (_jsxs(Field, { ...common, children: [label, help, _jsx(NativeSelect, { name: field.name, value: typeof value === 'string' ? value : '', options: field.options, placeholder: field.placeholder ?? messages.choose, onChange: (e) => setValue(field.name, e.target.value), onBlur: () => touch(field.name) }), err] }, field.name));
            case 'number':
                return (_jsxs(Field, { ...common, children: [label, help, _jsx(NumberControl, { field: field, value: value, onValue: (v) => setValue(field.name, v), onBlur: () => touch(field.name) }), err] }, field.name));
            case 'date':
                return (_jsxs(Field, { ...common, children: [label, help, _jsx(DatePicker, { name: field.name, value: typeof value === 'string' && value ? value : null, onValueChange: (v) => setValue(field.name, v), ...(field.min ? { min: field.min } : {}), ...(field.max ? { max: field.max } : {}), ...(locale ? { locale } : {}), onValidationChange: (e) => setDateErrors((d) => Boolean(d[field.name]) === Boolean(e) ? d : { ...d, [field.name]: Boolean(e) }), onBlur: () => touch(field.name) }), dateErrors[field.name] ? null : err] }, field.name));
            case 'textarea':
                return (_jsxs(Field, { ...common, children: [label, help, _jsx(Textarea, { name: field.name, value: typeof value === 'string' ? value : '', rows: field.rows ?? 3, placeholder: field.placeholder, maxLength: field.maxLength, onChange: (e) => setValue(field.name, e.target.value), onBlur: () => touch(field.name) }), err] }, field.name));
            default:
                return (_jsxs(Field, { ...common, children: [label, help, _jsx(Input, { name: field.name, type: field.inputType ?? 'text', value: typeof value === 'string' ? value : '', placeholder: field.placeholder, autoComplete: field.autoComplete, maxLength: field.maxLength, onChange: (e) => setValue(field.name, e.target.value), onBlur: () => touch(field.name) }), err] }, field.name));
        }
    };
    if (readOnly) {
        const shown = visibleFields(schema, values);
        return (_jsx("div", { className: cx('mn-schema-driven-form', className), "data-readonly": "", children: _jsx("dl", { className: "mn-schema-driven-form__view", children: shown.map((field) => (_jsxs("div", { className: "mn-schema-driven-form__view-row", children: [_jsx("dt", { children: field.label }), _jsx("dd", { children: formatSchemaValue(field, values[field.name], {
                                ...(locale ? { locale } : {}),
                                messages,
                            }) })] }, field.name))) }) }));
    }
    return (_jsxs("form", { ref: (node) => {
            formRef.current = node;
            if (typeof ref === 'function')
                ref(node);
            else if (ref)
                ref.current = node;
        }, noValidate: true, ...rest, "aria-busy": pending || undefined, onSubmit: submit, className: cx('mn-schema-driven-form', className), children: [schema.map((item) => {
                if (!isSection(item))
                    return renderField(item);
                const section = item;
                if (!isSchemaFieldVisible(section.visibleWhen, values))
                    return null;
                const descId = section.description ? `${auto}-${section.id}-desc` : undefined;
                return (_jsxs("fieldset", { className: "mn-schema-driven-form__section", "aria-describedby": descId, "data-section": section.id, children: [_jsx("legend", { className: "mn-schema-driven-form__legend", children: section.title }), section.description ? (_jsx("p", { id: descId, className: "mn-schema-driven-form__section-description", children: section.description })) : null, _jsx("div", { className: "mn-schema-driven-form__fields", children: section.fields.map(renderField) })] }, section.id));
            }), formError ? (_jsx(Alert, { tone: "danger", live: "assertive", className: "mn-schema-driven-form__error", children: formError })) : null, submitLabel !== null || secondaryActions ? (_jsxs("div", { className: "mn-schema-driven-form__actions", children: [submitLabel !== null ? (_jsx(Button, { type: "submit", variant: "primary", loading: pending, disabled: disabled, children: submitLabel ?? messages.submit })) : null, secondaryActions] })) : null] }));
});
//# sourceMappingURL=schema-driven-form.js.map